import React, { useState } from 'react'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { PrinterUtils, type PaperWidth } from '../../utils/PrinterUtils'

interface PrintDialogProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  onPrint: (_paperWidth: PaperWidth) => Promise<void> | void
  title?: string
  description?: string
  isLoading?: boolean
}

export const PrintDialog: React.FC<PrintDialogProps> = ({
  open,
  onOpenChange,
  onPrint,
  title = 'Configurar Impresión',
  description = 'Seleccione el ancho del papel para la impresión',
  isLoading = false,
}) => {
  const [selectedWidth, setSelectedWidth] = useState<PaperWidth>(() => {
    // Usar la configuración guardada como valor inicial
    return PrinterUtils.getPaperWidth()
  })

  const handlePrint = async () => {
    await onPrint(selectedWidth)
    onOpenChange(false)
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            🖨️ {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Selector de ancho */}
          <div>
            <h4 className="mb-3 font-medium">Ancho del papel:</h4>
            <div className="grid grid-cols-2 gap-3">
              <button
                className={`rounded-lg border-2 p-4 text-center transition-all ${
                  selectedWidth === 58
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => setSelectedWidth(58)}
                type="button"
              >
                <div className="font-semibold">58mm</div>
                <div className="text-xs opacity-75">Estándar</div>
                {selectedWidth === 58 && (
                  <Badge className="mt-2" variant="default">
                    Seleccionado
                  </Badge>
                )}
              </button>

              <button
                className={`rounded-lg border-2 p-4 text-center transition-all ${
                  selectedWidth === 80
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => setSelectedWidth(80)}
                type="button"
              >
                <div className="font-semibold">80mm</div>
                <div className="text-xs opacity-75">Ancho</div>
                {selectedWidth === 80 && (
                  <Badge className="mt-2" variant="default">
                    Seleccionado
                  </Badge>
                )}
              </button>
            </div>
          </div>

          {/* Información adicional */}
          <div className="rounded bg-blue-50 p-3">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> La configuración seleccionada se recordará
              para futuras impresiones.
            </p>
          </div>

          {/* Botones de acción */}
          <div className="flex gap-3">
            <Button
              className="flex-1"
              disabled={isLoading}
              onClick={handlePrint}
            >
              {isLoading ? 'Imprimiendo...' : '🖨️ Imprimir'}
            </Button>
            <Button
              disabled={isLoading}
              onClick={handleCancel}
              variant="outline"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PrintDialog
